import React from 'react';
import { Modal } from '../common/Modal';
import { User, Calendar, Building2, Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';

type RoomDetails = {
  roomNumber: string;
  type: 'd' | 't' | 'q';
  occupied: number;
  total: number;
  pilgrims: Array<{
    id: string;
    name: string;
    checkIn: number;
    checkOut: number;
    gender: 'M' | 'F';
    guideName: string;
    organizerName: string;
    age: number;
  }>;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  details: RoomDetails;
};

export const RoomDetailsModal = ({ isOpen, onClose, details }: Props) => {
  const { translations } = useLanguage();

  // Get room type label dynamically
  const getRoomTypeLabel = (type: string) => {
    return (
      translations?.roomDetailsModal?.roomTypes?.[type] ||
      {
        d: 'Double',
        t: 'Triple',
        q: 'Quadruple',
      }[type] ||
      type
    );
  };

  // Sort pilgrims by check-in date
  const sortedPilgrims = [...details.pilgrims].sort((a, b) => a.checkIn - b.checkIn);

  // Get age color class
  const getAgeColorClass = (age: number) => {
    if (age > 60) return 'bg-red-500';
    if (age > 40) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${translations?.roomDetailsModal?.room || 'Room'} ${details.roomNumber}`}
    >
      <div className="space-y-6">
        {/* Room Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">
              {translations?.roomDetailsModal?.roomInfo || 'Room Information'}
            </span>
            <span
              className={`px-2 py-1 rounded-full text-sm ${
                details.type === 'd'
                  ? 'bg-blue-100 text-blue-700'
                  : details.type === 't'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-purple-100 text-purple-700'
              }`}
            >
              {getRoomTypeLabel(details.type)}
            </span>
          </div>
          <div className="text-sm text-gray-600">
            {translations?.roomDetailsModal?.occupancy || 'Occupancy'}:{' '}
            {details.occupied}/{details.total}
          </div>
        </div>

        {/* Pilgrims List */}
        <div className="space-y-4">
          <h3 className="font-medium">
            {translations?.roomDetailsModal?.pilgrims || 'Pilgrims'}
          </h3>
          {sortedPilgrims.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {sortedPilgrims.map((pilgrim, index) => (
                <motion.div
                  key={`${pilgrim.id}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative p-4 rounded-lg border ${
                    pilgrim.gender === 'M' ? 'bg-blue-50 border-blue-200' : 'bg-pink-50 border-pink-200'
                  }`}
                >
                  {/* Pilgrim Name and Age */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        pilgrim.gender === 'M' ? 'bg-blue-100' : 'bg-pink-100'
                      }`}>
                        <User className={
                          pilgrim.gender === 'M' ? 'text-blue-500' : 'text-pink-500'
                        } size={20} />
                      </div>
                      <div>
                        <div className="font-medium">{pilgrim.name}</div>
                        <div className="text-sm text-gray-600">
                          {pilgrim.guideName} → {pilgrim.organizerName}
                        </div>
                      </div>
                    </div>
                    <span className={`
                      px-2 py-1 rounded-full text-xs text-white
                      ${getAgeColorClass(pilgrim.age)}
                    `}>
                      {pilgrim.age} {translations?.roomDetailsModal?.years || 'years'}
                    </span>
                  </div>

                  {/* Check-in/Check-out Dates */}
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-gray-400" />
                      <div>
                        <div>{translations?.roomDetailsModal?.checkIn || 'Check-in'}</div>
                        <div className="font-medium">
                          {new Date(pilgrim.checkIn).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-gray-400" />
                      <div>
                        <div>{translations?.roomDetailsModal?.checkOut || 'Check-out'}</div>
                        <div className="font-medium">
                          {new Date(pilgrim.checkOut).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                    <Clock size={16} />
                    <span>
                      {Math.ceil((pilgrim.checkOut - pilgrim.checkIn) / (1000 * 60 * 60 * 24))}{' '}
                      {translations?.roomDetailsModal?.days || 'days'}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {translations?.roomDetailsModal?.noPilgrims ||
                'No pilgrims in this room'}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};